/**
 * Axios error postprocessing
 *
 * @package Muval Frontend SPA
 * @author Tim Maier
 */
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { useClipboard } from '@vueuse/core';
import { isCanceledRequest, RequestCanceledError } from './requestCancellation';

export { isCanceledRequest, RequestCanceledError };

const { copy } = useClipboard({
    legacy: true,
});

/**
 * The path of a request URL, without the query string, fragment or userinfo.
 *
 * This value goes on the operator's clipboard, so anything a URL can carry that
 * we would not want pasted into a support thread is dropped. Axios usually keeps
 * request params in `config.params` rather than `config.url`, so in practice the
 * query is already absent — but a call that builds its own query string inline
 * would put an operator-entered search term (a customer name, email or phone)
 * straight into the copied text, and nothing stops one being written.
 *
 * `host` deliberately rather than `hostname`, so a non-default port survives;
 * userinfo lives in `username`/`password` and is dropped by not reading them.
 */
function requestPathForDiagnostics(url) {
    if (typeof url !== 'string' || url === '') {
        return null;
    }

    const RELATIVE_BASE = 'http://relative.invalid';

    try {
        const parsed = new URL(url, RELATIVE_BASE);

        return parsed.origin === RELATIVE_BASE ?
                parsed.pathname
            :   `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
    } catch {
        // URL is available in every browser the CRM supports, so this is only
        // reached for input that is not a URL at all. Still better than copying
        // it verbatim.
        return url.split(/[?#]/)[0];
    }
}

/**
 * Build the payload behind "Copy details" on a transport failure.
 *
 * An allowlist, deliberately, rather than trimming fields off the error. The
 * obvious `JSON.stringify({ error })` calls AxiosError.toJSON(), which
 * serialises the whole request config — and plugins/axios.js puts a live
 * `Authorization: Bearer <token>` in config.headers, so that would put a
 * working credential on the operator's clipboard. Axios only redacts when
 * config.redact is set, and nothing here sets it.
 *
 * It also fails better: a non-Axios Error has no enumerable own properties, so
 * `JSON.stringify({ error })` yields `{}`. Naming the fields always produces
 * something support can read.
 */
function copyableTransportFailure(error) {
    return JSON.stringify(
        {
            message: error?.message ?? null,
            code: error?.code ?? null,
            method: error?.config?.method ?? null,
            url: requestPathForDiagnostics(error?.config?.url),
            at: new Date().toISOString(),
        },
        null,
        2,
    );
}

/**
 * Shown for any 5xx. With APP_DEBUG off in production (CU-86d4b3rbg) the API
 * returns a bare "Server Error", which is not worth putting in front of an
 * operator, and the detail is in Sentry either way.
 *
 * 4xx deliberately keeps its own message. The switch below only has cases for
 * 401 and 422, so 403, 404, 409 and 429 all fall through to the same branch
 * carrying messages the operator does need to read.
 */
const SERVER_ERROR_MESSAGE = 'There was an error. Please contact support if the problem persists.';

/**
 * Shown whenever the API gives us nothing readable to show instead.
 */
const GENERIC_ERROR_MESSAGE = 'An error has occurred.';

/**
 * The message to put in front of the operator for a failed request.
 *
 * `||` rather than `??`, deliberately. A bare `abort(403)` raises an
 * HttpException carrying no message, and the non-debug JSON renderer emits
 * `{"message": ""}`. An empty string is neither null nor undefined, so `??`
 * never substitutes and `toast.error('')` paints a completely blank toast —
 * on a 403, alongside the redirect to /not_authorised, so the operator is
 * bounced with no stated reason and support has nothing to triage from
 * (CU-86d4bamvy).
 *
 * Trimmed first, because whitespace renders exactly as blank as an empty
 * string does. A non-string message falls back too: `[object Object]` in a
 * toast is worse than generic copy.
 *
 * Reads either error shape. `processError` is given the raw axios error, whose
 * body is at `.response.data`; every CRM caller downstream is given the object
 * this module returns, whose body is at `.data`. Handling both means a caller
 * with better copy of its own can reuse this rather than re-derive it and get
 * the guards wrong — the first attempt at one such call site hand-rolled
 * `error?.data?.message?.trim()`, which throws on a non-string message because
 * `?.` guards the message but not `.trim`.
 *
 * Exported so the task-status composables can adopt it rather than keep their
 * own copy of the same rule (CU-86d44jc4f).
 */
export function apiErrorMessage(error, fallback = GENERIC_ERROR_MESSAGE) {
    const message = error?.response?.data?.message ?? error?.data?.message;

    return (typeof message === 'string' ? message.trim() : '') || fallback;
}

/**
 * Client-side transport failures that are not actionable app bugs.
 * ERR_NETWORK covers XHR status 0 (offline, Wi‑Fi drop, sleep/wake); ERR_CANCELED is intentional abort.
 * ECONNABORTED covers a browser-aborted request and a request timeout, which axios gives the same code.
 * The abort is noise: MOVEPRO-CRM-25 (30 events). A timeout is a real availability signal and must keep
 * reporting — MOVEPRO-CRM-E3 is 1,195 login timeouts across 62 users, still firing (CU-86d477a1g).
 *
 * The two are separated on axios's message, not on `config.timeout`. That looks like the cleaner
 * discriminator and is wrong: axios emits the bare 'timeout exceeded' precisely WHEN `config.timeout` is
 * falsy (adapters/xhr.js, `request.ontimeout`), and `ontimeout` does fire with no timeout configured, which
 * is what MOVEPRO-CRM-E3 is. Testing `!config.timeout` would classify every one of those as noise.
 *
 * Only axios's exact `onabort` message counts as noise, so any ECONNABORTED we do not recognise keeps
 * reporting. That fails safe.
 * Keep in sync with Book `isNoiseAxiosError` in apps/book/src/utilities/axiosNoise.ts.
 */
const ABORTED_MESSAGE = 'Request aborted';

function isNoiseAxiosError(error) {
    if (error?.code === 'ERR_CANCELED' || error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        return true;
    }

    return error?.code === 'ECONNABORTED' && error?.message === ABORTED_MESSAGE;
}

/**
 * A 402 means the request was fine and the bank said no. A declined card is a
 * routine operator outcome, not an app fault, so it must not become a Sentry
 * event — at the rate cards decline it would bury genuine errors.
 */
function isDeclinedPayment(error) {
    return error?.response?.status === 402;
}

/**
 * Helper function to process Axios errors
 *
 * @param error Axios error object
 * @return object Error Muval SFC can use
 */
export function processError(error) {
    // Intentional aborts (route change, superseded request) are noise, not app errors.
    // Handle BEFORE the response/request branches: axios can reject an already-aborted
    // signal with ERR_CANCELED before it creates error.request. A bare `return` made
    // the interceptor Promise.reject(undefined), and form catch blocks that read
    // `error.message` threw TypeError (MOVEPRO-CRM-MA). Handling cancel only inside
    // the error.request branch would let a pre-dispatch cancel fall through to the
    // setup-error path and return code: null, which callers treat as a real form error.
    if (isCanceledRequest(error)) {
        return new RequestCanceledError(error?.message || 'canceled');
    }

    if (
        import.meta.env.VITE_ENV == 'production' &&
        !isNoiseAxiosError(error) &&
        !isDeclinedPayment(error) &&
        error instanceof Error
    ) {
        // The API returns its own Sentry event id on an unhandled 500. Tagging our
        // event with it is the only thing joining this browser-side event to the API
        // exception behind it: the two live in separate Sentry projects, and with
        // APP_DEBUG off the response body carries no other detail (CU-86d4b4pre).
        const apiEventId = error?.response?.data?.sentry_event_id;

        Sentry.captureException(error, apiEventId ? { tags: { api_event_id: apiEventId } } : undefined);
    }

    let result = {
        code: null,
        message: 'Unknown error',
        errors: null,
        links: null,
        data: null,
    };

    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx

        switch (error.response.status) {
            case 401:
                result.message = apiErrorMessage(error);
                break;

            case 422:
                // toast.error(apiErrorMessage(error)); // Disabled the toast notif for 422 errors.
                result.message = apiErrorMessage(error);
                result.errors = error.response.data.errors;
                result.links = error.response.data.links;
                break;

            default:
                if (error.stack) {
                    delete error.stack;
                }

                if (error?.config?.xSilent === true) {
                    console.log('Skipped toast as request was set to silent:', apiErrorMessage(error));
                } else if (!useUserStore().isAuthenticated) {
                    console.log('Skipped toast as user is not authenticated:', apiErrorMessage(error));
                } else if (error.response.status >= 500) {
                    toast.error(SERVER_ERROR_MESSAGE);
                } else {
                    toast.error(apiErrorMessage(error));
                }

                // Generic on 5xx here too, not just in the toast above. The axios
                // interceptor rejects with this object, and 261 places in the CRM
                // read a caught error's `.message` — several passing it straight
                // to a toast (useQuoteHelper, useTaskStatusMutation,
                // useCrewTaskStatusMutation, useSalesActionNotes). Leaving the raw
                // value here would route the server's own text around the generic
                // toast, which is the whole point of the change. It matters most
                // for the controllers that return `$e->getMessage()` in their own
                // body and so bypass APP_DEBUG entirely (CU-86d2jwmhz).
                result.message = error.response.status >= 500 ? SERVER_ERROR_MESSAGE : apiErrorMessage(error);
                result.errors = error?.response?.data?.errors;

                break;
        }

        result.code = error.response.status;
        result.data = error.response.data;
    } else if (error.request) {
        // The request was made but no response was received. Cancellations are
        // handled at the top of this function, before this branch.

        if (error.stack) {
            delete error.stack;
        }

        result.message = error?.message ?? 'Network Error';
        result.code = error?.code ?? 'ERR_NETWORK';

        if (error?.config?.xSilent === true) {
            console.log('Skipped toast as request was set to silent:', result.message);
        } else if (!useUserStore().isAuthenticated) {
            console.log('Skipped toast as user is not authenticated:', result.message);
        } else if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            console.log('Skipped toast as browser is offline:', result.message);
        } else {
            // Kept here, unlike the response branch: a transport failure never
            // reaches the API, so there is no server-side event to correlate
            // with, and ERR_NETWORK is deliberately excluded from Sentry as
            // noise. The copied text is the only diagnostic there is.
            toast.error('An error has occurred.', {
                action: {
                    label: 'Copy details',
                    onClick: () => copy(copyableTransportFailure(error)),
                },
            });
        }
    } else {
        // Something happened in setting up the request that triggered an Error
        result.message = 'Axios error: ' + error.message;
    }

    return result;
}
